import { trackCall } from "@/lib/track";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Mail, Phone, ShieldCheck, Fuel, Gauge, Cog, Car as CarIcon, MapPin, CheckCircle2 } from "lucide-react";
import { vehiclePhotoUrl, formatPrice } from "@/pages/VehicleListPage";

const BODY_TYPE_LABELS = { sedan: "Sedan", suv: "SUV", ute: "Ute", hatch: "Hatch", wagon: "Wagon", van: "Van", coupe: "Coupe", convertible: "Convertible", other: "Other" };
const TRANSMISSION_LABELS = { auto: "Automatic", manual: "Manual" };
const FUEL_LABELS = { petrol: "Petrol", diesel: "Diesel", hybrid: "Hybrid", ev: "Electric", lpg: "LPG" };
const REGO_LABELS = { active: "Registered", expired: "Rego expired", unregistered: "Unregistered" };

export default function VehicleDetailPage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    api.get(`/vehicles/${id}`)
      .then((r) => {
        setV(r.data);
        const d = r.data;
        document.title = `${d.title} — ${d.status === "rent" ? "For Rent" : "For Sale"} | Tassie Automotive Glenorchy`;
        const tag = document.querySelector('meta[name="description"]');
        if (tag) tag.setAttribute("content", `${d.title} (${d.year}) ${d.status === "rent" ? "for rent" : "for sale"} at Tassie Automotive, Glenorchy TAS — workshop-checked, ${formatPrice(d.price)}${d.status === "rent" ? "/day" : ""}. Photos, full specs and enquiry online.`);
      })
      .catch((e) => setErr(e.response?.status === 404 ? "Vehicle not found" : "Could not load"));
  }, [id]);

  if (err) return <NotFoundView />;
  if (!v) return <div className="max-w-7xl mx-auto p-20 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;

  const photo = v.photos[activePhoto] || null;
  const isRent = v.status === "rent";

  return (
    <div data-testid="vehicle-detail" className="bg-background">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
        <Link to={isRent ? "/cars-for-rent" : "/cars-for-sale"} className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand-red font-mono text-xs uppercase tracking-widest">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to {isRent ? "rentals" : "sale list"}
        </Link>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Photo gallery */}
        <div className="lg:col-span-7">
          <div className="aspect-[16/10] bg-zinc-950 border border-zinc-900 overflow-hidden">
            {photo ? (
              <img data-testid="vehicle-active-photo" src={vehiclePhotoUrl(photo.url)} alt={v.title} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full grid place-items-center text-zinc-700 font-mono text-xs uppercase tracking-widest">No photos yet</div>
            )}
          </div>
          {v.photos.length > 1 && (
            <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
              {v.photos.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`vehicle-thumb-${i}`}
                  onClick={() => setActivePhoto(i)}
                  className={`aspect-[4/3] overflow-hidden border ${i === activePhoto ? "border-brand-red" : "border-zinc-900 hover:border-zinc-700"}`}
                >
                  <img loading="lazy" decoding="async" src={vehiclePhotoUrl(p.url)} alt={`${v.title} ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: info + enquiry */}
        <div className="lg:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">
            {isRent ? "For rent" : "For sale"}{v.is_sold && " · SOLD"}
          </div>
          <h1 className="mt-3 font-display uppercase text-4xl tracking-tighter text-white leading-[0.95]">{v.title}</h1>
          <div className="mt-4 font-display text-4xl text-brand-red">
            {formatPrice(v.price)}{isRent && <span className="text-sm text-zinc-400"> /day</span>}
            {isRent && v.weekly_rate ? <span className="ml-3 text-base text-zinc-400">or {formatPrice(v.weekly_rate)}/week</span> : null}
          </div>

          <ul className="mt-6 grid grid-cols-2 gap-y-2 text-sm text-zinc-300">
            <Spec label="Year" value={v.year} />
            {v.kilometres != null && <Spec label="Kilometres" value={`${Intl.NumberFormat("en-AU").format(v.kilometres)} km`} icon={Gauge} />}
            {v.body_type && <Spec label="Body" value={BODY_TYPE_LABELS[v.body_type]} icon={CarIcon} />}
            {v.transmission && <Spec label="Transmission" value={TRANSMISSION_LABELS[v.transmission]} icon={Cog} />}
            {v.fuel && <Spec label="Fuel" value={FUEL_LABELS[v.fuel]} icon={Fuel} />}
            {v.rego_status && <Spec label="Rego" value={REGO_LABELS[v.rego_status]} />}
            {v.vin && <Spec label="VIN" value={v.vin} />}
          </ul>

          {isRent && (
            <div data-testid="vehicle-rent-terms" className="mt-6 border border-brand-red/40 bg-brand-red/5 p-4 text-sm text-zinc-200 space-y-1">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-brand-red">
                <ShieldCheck className="h-3.5 w-3.5" /> Rental terms
              </div>
              <p>{formatPrice(v.bond_amount ?? 500)} refundable bond · Minimum hire {v.min_rent_days ?? 7} days{v.max_rent_days ? ` · Max ${v.max_rent_days} days` : ""} · Conditions apply.</p>
            </div>
          )}

          {v.description && (
            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">About this vehicle</div>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{v.description}</p>
            </div>
          )}

          <EnquiryForm vehicle={v} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="vehicle-detail-seo-copy">
        <div className="border-t border-zinc-900 pt-10 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl">
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">
              {isRent ? "Renting from a real workshop" : "Buying with confidence"}
            </h2>
            {isRent ? (
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                This {v.title} is part of the small rental fleet we maintain in-house at our Repco Authorised workshop
                in Glenorchy. Every rental goes out freshly checked by our own mechanics — brakes, tyres, fluids and
                lights — so you're not gambling on a car someone else neglected. Daily and weekly rates are available,
                pick-up is from 46 Grove Rd Glenorchy, and if anything plays up during your hire you call the people
                who actually service the car.
              </p>
            ) : (
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                This {v.title} has been through our own Repco Authorised workshop at 46 Grove Rd, Glenorchy — inspected
                and honestly described by the same mechanics who can service it for you after the sale. You're welcome
                to look over the car in person, take it for a drive and ask us anything about its history and
                condition. We'd rather tell you the truth about a car than sell you the wrong one.
              </p>
            )}
          </div>
          <div>
            <h2 className="font-display uppercase text-2xl tracking-tight text-white">Questions? Just ask.</h2>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Send an enquiry with the form above or call (03) 6288 7861 during opening hours — Monday to Friday
              8am–5pm, Saturday 9am–5pm, Sunday 10am–5pm. We usually reply the same business day. If this
              {isRent ? " rental is booked out for your dates" : " car isn't quite right"}, tell us what you're
              after and we'll keep an eye out — stock and availability change regularly. We can also arrange an
              independent pre-purchase inspection on any vehicle you're considering elsewhere in Hobart.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const Spec = ({ label, value, icon: Icon }) => (
  <li className="flex items-center gap-2">
    {Icon && <Icon className="h-3.5 w-3.5 text-brand-red" />}
    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <span className="text-zinc-200 font-semibold">{value}</span>
  </li>
);

function EnquiryForm({ vehicle }) {
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", message: "", preferred_dates: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/enquire`, form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch shortly");
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: "AW-17489509584/lWAaCKGajZkcEND50pNB", value: 1.0, currency: "AUD" });
      }
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Too many enquiries from this network — wait a minute and try again.");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again or call us.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="enquiry-success" className="mt-8 border border-brand-red bg-brand-red/10 p-6 text-center">
        <CheckCircle2 className="h-7 w-7 text-brand-red mx-auto mb-3" />
        <div className="font-display text-xl text-white uppercase tracking-tight">Enquiry sent.</div>
        <p className="mt-2 text-sm text-zinc-300">We&apos;ll contact you within one business day.</p>
      </div>
    );
  }

  const isRent = vehicle.status === "rent";

  return (
    <form data-testid="vehicle-enquiry-form" onSubmit={submit} className="mt-8 border border-zinc-900 bg-zinc-950 p-6 space-y-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-brand-red mb-1">
        {isRent ? "Request this rental" : "Enquire about this car"}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="enquiry-name" required className="ta-input" placeholder="Your name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
        <input data-testid="enquiry-phone" required className="ta-input" placeholder="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
      </div>
      <input data-testid="enquiry-email" required type="email" className="ta-input" placeholder="Email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
      {isRent && (
        <input data-testid="enquiry-dates" className="ta-input" placeholder="Preferred dates (e.g. 12 → 19 Aug)" value={form.preferred_dates} onChange={(e) => setForm({ ...form, preferred_dates: e.target.value })} />
      )}
      <textarea data-testid="enquiry-message" className="ta-input min-h-[100px]" placeholder={isRent ? "Any questions? Tow load? Drivers?" : "Any questions about this car?"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <button data-testid="enquiry-submit" type="submit" disabled={submitting} className="w-full px-6 py-3 bg-brand-red text-white font-bold text-xs uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50">
        {submitting ? "Sending…" : (isRent ? "Request rental" : "Send enquiry")}
      </button>
      <div className="flex items-center gap-4 text-zinc-500 text-xs font-mono uppercase tracking-widest pt-2 border-t border-zinc-900">
        <a href="tel:+61362887861" onClick={() => trackCall("vehicle_detail")} className="inline-flex items-center gap-1.5 hover:text-brand-red"><Phone className="h-3 w-3" /> (03) 6288 7861</a>
        <a href="mailto:tassieauto@gmail.com" className="inline-flex items-center gap-1.5 hover:text-brand-red"><Mail className="h-3 w-3" /> Email</a>
        <span className="inline-flex items-center gap-1.5"><MapPin className="h-3 w-3 text-brand-red" /> Glenorchy</span>
      </div>
    </form>
  );
}

const NotFoundView = () => (
  <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
    <h1 className="font-display uppercase text-4xl text-white">Vehicle not found.</h1>
    <p className="mt-4 text-zinc-400">It may have been sold or removed. Check our latest stock:</p>
    <div className="mt-6 flex justify-center gap-3">
      <Link to="/cars-for-sale" className="px-5 py-3 border border-zinc-700 text-white text-xs font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red">Cars for sale</Link>
      <Link to="/cars-for-rent" className="px-5 py-3 border border-zinc-700 text-white text-xs font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red">Cars for rent</Link>
    </div>
  </div>
);
